import { getAccessToken, getClientId } from './authService';
import { API_BASE, API_KEY, CLIENT_APP, CLIENT_NAME } from './clientConfig';
import axios from 'axios';

const STORAGE_API_V1 = `${API_BASE}/storage-service/api/v1`;

const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic'];

function isImage(filename = '') {
  const lower = filename.toLowerCase();
  return IMAGE_EXTS.some((ext) => lower.endsWith(ext));
}

function guessContentType(filename = '') {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.heic')) return 'image/heic';
  return 'image/jpeg';
}

// Use only allowed fileType values: PHOTO, DOCUMENT, etc.
const guessFileType = (file: File): string => {
  if (file.type.startsWith('image/')) return 'PHOTO';
  if (file.type === 'application/pdf') return 'DOCUMENT';
  return 'DOCUMENT';
};

type StorageUploadPurpose =
  | 'USER_PROFILE'
  | 'TASK_PROFILE'
  | 'TASK_PHOTOS'
  | 'TASK_COMMENTS'
  | 'TASK_CANCEL'
  | 'HELPER_REVIEW'
  | 'REQUESTER_REVIEW'
  | 'TASK_REVIEW'
  | 'OTHER';

export type UploadPurpose =
  | 'USER_PROFILE'
  | 'IDENTITY_VERIFICATION'
  | 'WORK_AUTHORIZATION'
  | 'TASK_PROFILE'
  | 'TASK_PHOTOS'
  | 'TASK_COMMENTS'
  | 'TASK_CANCEL'
  | 'HELPER_REVIEW'
  | 'REQUESTER_REVIEW'
  | 'TASK_REVIEW'
  | 'OTHER'
  | 'BUSINESS_LOGO'
  | 'BUSINESS_DOCUMENT'
  | 'BUSINESS_CERTIFICATE'
  | 'LOCATION_PHOTO';

const storageUploadPurposes = new Set<StorageUploadPurpose>([
  'USER_PROFILE',
  'TASK_PROFILE',
  'TASK_PHOTOS',
  'TASK_COMMENTS',
  'TASK_CANCEL',
  'HELPER_REVIEW',
  'REQUESTER_REVIEW',
  'TASK_REVIEW',
  'OTHER',
]);

const resolveUploadPurpose = (purpose: UploadPurpose): StorageUploadPurpose => {
  if (purpose === 'IDENTITY_VERIFICATION' || purpose === 'WORK_AUTHORIZATION') {
    return 'USER_PROFILE';
  }

  return storageUploadPurposes.has(purpose as StorageUploadPurpose)
    ? (purpose as StorageUploadPurpose)
    : 'OTHER';
};

type UploadFileOptions = {
  purpose?: UploadPurpose;
  fileType?: string;
  confirm?: boolean;
};

type UploadIntent = {
  file: {
    id: string;
    [key: string]: any;
  };
  uploadUrl: string;
  method?: string;
  contentType?: string;
  headers?: Record<string, string>;
};

async function compressImageToTarget(file: File, opts: any = {}) {
  const {
    maxBytes = 500_000,
    maxWidth = 1920,
    maxHeight = 1920,
    initialQuality = 0.8,
    minQuality = 0.5,
    stepQuality = 0.1,
    scaleStep = 0.9,
    forceJpeg = true,
  } = opts;

  const getBlob = (canvas: HTMLCanvasElement, quality: number) =>
    new Promise<Blob>((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob!),
        forceJpeg ? 'image/jpeg' : file.type,
        quality
      );
    });

  return new Promise<{ blob: Blob; size: number; contentType: string }>(
    (resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        let width = img.width;
        let height = img.height;
        const aspect = width / height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            width = maxWidth;
            height = Math.floor(width / aspect);
          } else {
            height = maxHeight;
            width = Math.floor(height * aspect);
          }
        }

        let quality = initialQuality;
        const draw = () => {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx!.drawImage(img, 0, 0, width, height);
          return canvas;
        };

        let canvas = draw();
        let blob = await getBlob(canvas, quality);
        let size = blob.size;

        while (
          size > maxBytes &&
          (quality > minQuality || width > 640 || height > 640)
        ) {
          if (quality > minQuality) {
            quality = Math.max(minQuality, quality - stepQuality);
          } else {
            width = Math.floor(width * scaleStep);
            height = Math.floor(height * scaleStep);
          }
          canvas = draw();
          blob = await getBlob(canvas, quality);
          size = blob.size;
        }

        if (size > file.size) {
          resolve({ blob: file, size: file.size, contentType: file.type });
        } else {
          resolve({
            blob,
            size,
            contentType: forceJpeg ? 'image/jpeg' : file.type,
          });
        }
      };
      img.onerror = (e) => reject(e);
      img.src = URL.createObjectURL(file);
    }
  );
}

export const uploadFile = async (
  userId: string | undefined,
  file: File,
  { purpose = 'USER_PROFILE', fileType, confirm = false }: UploadFileOptions = {}
) => {
  const token = getAccessToken();
  if (!token) throw new Error('You must be signed in to upload files.');

  const clientId = await getClientId();

  const actualPurpose = resolveUploadPurpose(purpose);
  const actualFileType = fileType || guessFileType(file);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-NAME': CLIENT_NAME,
    ...(clientId ? { 'O-CLIENT-ID': clientId } : {}),
    ...(userId ? { 'O-ACTOR-ID': userId } : {}),
  };

  let uploadBlob: Blob = file;
  let contentType = file.type || guessContentType(file.name);
  let fileSize = file.size;

  if (isImage(file.name)) {
    const compressed = await compressImageToTarget(file, { forceJpeg: false });
    uploadBlob = compressed.blob;
    fileSize = compressed.size;
    contentType = compressed.contentType;
  }

  // 1. Upload intent
  let intent: UploadIntent;
  try {
    const intentResponse = await axios.post(
      `${STORAGE_API_V1}/files/upload-intent`,
      {
        filename: file.name,
        fileType: actualFileType,
        size: fileSize,
        purpose: actualPurpose,
      },
      { headers }
    );
    intent = intentResponse.data;
  } catch (error: any) {
    const errText = error.response?.data || error.message;
    throw new Error(errText || 'Failed to create upload intent.');
  }

  if (!intent.uploadUrl || !intent.file?.id) {
    throw new Error('Upload intent did not include upload details.');
  }

  const fileId = intent.file.id;

  const uploadMethod = intent.method || 'PUT';
  const uploadContentType = intent.contentType || contentType || 'application/octet-stream';
  const uploadHeaders = {
    'Content-Type': uploadContentType,
    ...(intent.headers || {}),
  };

  // 2. Upload to GCS using the exact method and headers returned by upload-intent.
  try {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(uploadMethod, intent.uploadUrl, true);
      Object.entries(uploadHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Presigned upload failed: ${xhr.status} ${xhr.responseText}`));
      };
      xhr.onerror = () => reject(new Error(`Presigned upload network error: ${xhr.status}`));
      xhr.ontimeout = () => reject(new Error('Presigned upload timed out.'));
      xhr.send(uploadBlob);
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to upload file to GCS.');
  }

  if (confirm) {
    // 3. Confirm upload through the same single-file endpoint used by Olreadia.
    try {
      await axios.patch(
        `${STORAGE_API_V1}/files/${fileId}/confirm-upload`,
        null,
        { headers }
      );
    } catch (error: any) {
      const errText = error.response?.data || error.message;
      throw new Error(`File upload confirmation failed: ${errText}`);
    }
  }

  return {
    fileId,
    file: intent.file,
  };
};
